// « Contrôles » — the window-1 card findings (PMS-LEDGER-L0B-BRIEF §3.5).
//
// Before Vega closes the day, what is wrong between the card payments keyed in
// Vega and the taps the terminal recorded. Read-only: the desk fixes Vega, and a
// finding vanishes when Vega is right (no « seen », no memory: dispositions are
// a later increment).
//
// A stale feed is shown as the L0 banner and NO list: « flux en retard » must
// never read as « rien à signaler ».

import type { ReactNode } from 'react';
import type { CardFinding, IsoDate } from '@lemontaigu/pms-core';
import { LedgerGateBanner } from './LedgerGateBanner.js';
import { SheetTop } from './SheetTop.js';
import { DayBar } from './DayBar.js';
import { fmtAmount } from './logic.js';
import { fmtDate } from './logic.js';
import { CLASS_TITLE_FR, groupByClass } from './cardFindingsView.js';
import type { CardFindingsState } from './CardFindingsContext.js';

const clock = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
    : '';

function Row({ f }: { f: CardFinding }) {
  return (
    <tr className={f.state === 'awaiting_remise' ? 'pms-muted' : undefined}>
      <td>
        {f.partyLabel ?? '—'}
        {f.reference && <span className="pms-sub"> {f.reference}</span>}
      </td>
      <td className="pms-num">{fmtAmount(f.amountCents)}</td>
      <td className="pms-nowrap">{clock(f.tappedAt) || clock(f.writtenAt)}</td>
      <td>
        {f.explanation}
        {f.state === 'overdue' && <span className="pms-sub"> · délai passé : à voir avec la clôture</span>}
      </td>
    </tr>
  );
}

export interface ControlesProps {
  day: IsoDate;
  onDay: (day: IsoDate) => void;
  today: IsoDate;
  state: CardFindingsState & { allowed: boolean };
  nav?: ReactNode;
}

export function Controles({ day, onDay, today, state, nav }: ControlesProps) {
  const { day: answer, error, loading, allowed } = state;
  const groups = answer ? groupByClass(answer.findings) : [];

  return (
    <section className="pms-print" aria-label="Contrôles">
      <SheetTop nav={nav} onRefresh={() => undefined} canPrint={false} />
      <DayBar value={day} onChange={onDay} today={today} label="Jour contrôlé" />
      <div className="pms-head">
        <div>
          <h2>Contrôles des paiements carte — {fmtDate(day)}</h2>
          <p className="pms-hint">
            Vega contre les débits du terminal, avant la clôture de Vega. Un écart se corrige dans Vega : il disparaît
            de cette liste à la lecture suivante.
          </p>
        </div>
      </div>

      <LedgerGateBanner day={day} />

      {!allowed && <p className="pms-warn">Ces contrôles ne sont pas disponibles pour ce compte.</p>}
      {error && <p className="pms-warn">⚠ {error} — ne pas conclure à l’absence d’écart.</p>}
      {loading && !answer && <p className="pms-hint">Lecture…</p>}

      {answer && answer.gateClosedReason === null && groups.length === 0 && (
        <p className="pms-hint">Aucun écart pour ce jour.</p>
      )}

      {groups.map(({ cls, items }) => (
        <div key={cls}>
          <h3>
            {CLASS_TITLE_FR[cls]} <span className="pms-sub">({items.length})</span>
          </h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>Réservation</th>
                <th className="pms-num">Montant</th>
                <th>Heure</th>
                <th>Écart</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f, i) => (
                <Row key={`${cls}-${f.reference ?? 'tap'}-${f.writtenAt ?? f.tappedAt}-${i}`} f={f} />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
